import { Component, computed, DestroyRef, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet, ActivatedRoute, Router } from '@angular/router';
import { PhDetailsInfoTap } from './ph-details-info-tap/ph-details-info-tap';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { ApprovedPharmaciesService } from '../../../../services/pharmacy/approved-pharmacies.service';
@Component({
  selector: 'app-ph-details-component',
  imports: [
    RouterOutlet,
    RouterLinkActive,
    RouterLink,
    PhDetailsInfoTap,
    SubmitButton,
    ConfirmationDialog,
    SuccessfulNotification,
  ],
  templateUrl: './ph-details-component.html',
  styleUrl: './ph-details-component.scss',
})
export class PhDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private approvedPharmaciesService = inject(ApprovedPharmaciesService);
  public locale = inject(LOCALE_ID);
  pharmacyId = signal<number | null>(null);
  isOrdersStopped = signal<boolean>(false);
  pharmacyName = signal<string>('');
  currentPharmacyName = computed(() => this.pharmacyName());
  breadcrumbText = computed(() => {
    return ` ${this.currentPharmacyName()}`;
  });
  tabs = signal([
    { label: $localize`:@@tabPharmacyInfo:Pharmacy Information` },
    { label: $localize`:@@tabCartDetails:Cart Details` },
  ]);
  activeTabIndex = signal<number>(0);
  showSuspendDialog = signal<boolean>(false);
  pendingSuspendValue = signal<boolean | null>(null);
  suspendLoading = signal<boolean>(false);
  suspendError = signal<string>('');
  showSuccess = signal<boolean>(false);
  successMessage = signal<string>('');
  hasTeleSalesEmployee = signal<boolean>(true);
  assignButtonText = $localize`:@@assignTeleSales:Assign Tele Sales Employee`;
  reassignButtonText = $localize`:@@reassignTeleSales:Reassign Tele Sales Employee`;
  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    const pharmacyName = history.state?.pharmacyName;
    if (id) {
      this.pharmacyId.set(Number(id));
    }
    this.pharmacyName.set(pharmacyName ?? '');
  }
  isArabic(): boolean {
    return this.locale.startsWith('ar');
  }
  suspendDialogTitle = computed(() => {
    const suspending = this.pendingSuspendValue();
    return this.isArabic()
      ? suspending
        ? 'إيقاف طلبات الصيدلية'
        : 'إعادة تفعيل الطلبات'
      : suspending
        ? 'Suspend Pharmacy Orders'
        : 'Reactivate Orders';
  });
  suspendDialogDescription = computed(() => {
    const suspending = this.pendingSuspendValue();
    return this.isArabic()
      ? suspending
        ? 'هل أنت متأكد من إيقاف الطلبات لهذه الصيدلية؟'
        : 'هل أنت متأكد من إعادة تفعيل الطلبات لهذه الصيدلية؟'
      : suspending
        ? 'Are you sure you want to suspend ordering for this pharmacy?'
        : 'Are you sure you want to reactivate ordering for this pharmacy?';
  });
  suspendDialogConfirmContent = computed(() => {
    const suspending = this.pendingSuspendValue();
    return this.isArabic() ? (suspending ? 'إيقاف' : 'إعادة تفعيل') : suspending ? 'Suspend' : 'Reactivate';
  });
  suspendDialogCancelContent = computed(() => (this.isArabic() ? 'إلغاء' : 'Cancel'));
  selectTab(index: number): void {
    this.activeTabIndex.set(index);
  }
  toggleOrdersStatus(event: Event): void {
    const input = event.target as HTMLInputElement;
    const nextValue = input.checked;
    input.checked = this.isOrdersStopped();
    this.pendingSuspendValue.set(nextValue);
    this.suspendError.set('');
    this.showSuspendDialog.set(true);
  }
  closeSuspendDialog(): void {
    this.showSuspendDialog.set(false);
    this.pendingSuspendValue.set(null);
    this.suspendError.set('');
  }
  onSuspendDialogSubmit(): void {
    const id = this.pharmacyId();
    const suspending = this.pendingSuspendValue();
    if (id === null || suspending === null) {
      return;
    }
    this.suspendLoading.set(true);
    this.suspendError.set('');
    const request$ = suspending
      ? this.approvedPharmaciesService.suspendPharmacy(id)
      : this.approvedPharmaciesService.unsuspendPharmacy(id);
    const subscription = request$.subscribe({
      next: () => {
        this.isOrdersStopped.set(suspending);
        this.suspendLoading.set(false);
        this.showSuspendDialog.set(false);
        this.pendingSuspendValue.set(null);
        this.successMessage.set(
          this.isArabic()
            ? suspending
              ? `تم إيقاف طلبات الصيدلية '${this.currentPharmacyName()}' بنجاح`
              : `تم إعادة تفعيل طلبات الصيدلية '${this.currentPharmacyName()}' بنجاح`
            : suspending
              ? `Pharmacy '${this.currentPharmacyName()}' orders suspended successfully`
              : `Pharmacy '${this.currentPharmacyName()}' orders reactivated successfully`,
        );
        this.showSuccess.set(true);
        setTimeout(() => {
          this.showSuccess.set(false);
        }, 3000);
      },
      error: (err) => {
        this.suspendLoading.set(false);
        const backendError = err?.error;
        this.suspendError.set(
          (this.isArabic() ? backendError?.messageAr : backendError?.messageEn) ?? backendError?.message ?? '',
        );
      },
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
  openAssignModal(): void {
    console.log('Open Assign Modal');
  }
  openReassignModal(): void {
    console.log('Open Reassign Modal');
  }

  isTeleSalesAssigned(isTeleSales: boolean) {
    this.hasTeleSalesEmployee.set(isTeleSales);
  }
}
